import React, { useCallback, useMemo, useState } from 'react';
import { FormControl, FormLabel, MenuItem, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../MeetingStaffContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../../MotionVotingControls';
import { Motion } from 'store/types/Meeting';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  useSafeAttendeeDisplayValue,
} from 'util/meeting';

interface EditorialChangePanelProps {
  validate?: () => boolean;
  onChange?: (motion: Motion) => void;
  initialEditorialChange?: Pick<Motion, 'fromText' | 'toText' | 'justification'>;
}

const idLabel = 'motion';
const defaultStatement = 'To approve editorial change(s).';

const EditorialChangeSection: React.FunctionComponent<EditorialChangePanelProps> = ({
  validate,
  onChange,
  initialEditorialChange,
}) => {
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { currentDocument } = useMeetingStaffContext();
  const [draftMotionId, setDraftMotionId] = useState(() => `${idLabel}-${Date.now()}`);
  const [fromText, setFromText] = useState(initialEditorialChange?.fromText || '');
  const [toText, setToText] = useState(initialEditorialChange?.toText || '');
  const [justification, setJustification] = useState(initialEditorialChange?.justification || '');
  const [statement, setStatement] = useState(defaultStatement);
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: draftMotionId,
      title: 'Editorial Change',
      documentId: currentDocument?.id,
      fromText,
      toText,
      justification,
      statement,
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [currentDocument?.id, discussion, draftMotionId, fromText, justification, safeBy, safeSecond, statement, toText]);

  React.useEffect(() => {
    onChange?.(currentMotion);
  }, [currentMotion, onChange]);

  const handleClearMotion = useCallback(() => {
    setDraftMotionId(`${idLabel}-${Date.now()}`);
    setFromText('');
    setToText('');
    setJustification('');
    setStatement(defaultStatement);
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  return (
    <GridContainer>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>From *</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id={`editorial-change-from-text-editor-${draftMotionId}`}
            value={fromText}
            onEditorChange={(newValue: string) => setFromText(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>To *</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id={`editorial-change-to-text-editor-${draftMotionId}`}
            value={toText}
            onEditorChange={(newValue: string) => setToText(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Justification</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id={`editorial-change-justification-editor-${draftMotionId}`}
            value={justification}
            onEditorChange={(newValue: string) => setJustification(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <TextField
          label={'Motion'}
          value={statement}
          required
          onChange={(event) => {
            setStatement(event.target.value);
          }}
        ></TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'By'}
          select
          required
          value={safeBy}
          onChange={(event) => {
            setBy(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {byAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem md={6}>
        <TextField
          label={'Second'}
          select
          required
          value={safeSecond}
          onChange={(event) => {
            setSecond(event.target.value);
          }}
        >
          <MenuItem value={''}>-- None --</MenuItem>
          {secondAttendeeOptions}
        </TextField>
      </GridItem>
      <GridItem>
        <FormControl className="w-100">
          <FormLabel>Discussion</FormLabel>
          <Editor
            {...defaultTinyMCEProps}
            id={`editorial-change-discussion-editor-${draftMotionId}`}
            value={discussion}
            onEditorChange={(newValue: string) => setDiscussion(newValue)}
            init={{
              ...defaultTinyMCEProps.init,
              images_upload_handler: createImagesUploadHandler(meetingId),
            }}
          />
        </FormControl>
      </GridItem>
      <GridItem>
        <MotionVotingControls
          currentMotion={currentMotion}
          requiredFields={['fromText', 'toText', 'statement', 'by', 'second']}
          validate={validate}
          onClearMotion={handleClearMotion}
          showVotingRuleSelector={false}
        />
      </GridItem>
    </GridContainer>
  );
};

export default EditorialChangeSection;
